import { useMemo, useRef, useState } from 'react'
import { ChevronDown, ChevronRight, Trash2 } from 'lucide-react'
import { AdvancedDisclosure, Button, HelpTip, Select, Switch } from '../common'
import TokenField from './TokenField'
import useInspectFocus from './useInspectFocus'
import { labelForDisplayType } from '../../constants/displayTypes'
import {
  BASIC_COLOR_FIELDS,
  STATE_COLOR_FIELDS,
  GRADIENT_FIELDS,
  TYPOGRAPHY_GROUPS,
  TYPOGRAPHY_SUBFIELDS,
  LAYOUT_GROUPS,
  EFFECT_FIELDS,
  COMPONENT_GROUPS,
  PRESENTATION_GROUPS,
  ASSET_FIELDS,
  inferKindFromPath,
  getBaselineTokenPaths,
} from '@/config/brandingTokens'
import {
  getSurfaceManifest,
  getAllSurfaceTokenPaths,
} from '@/config/surfaceTokenManifests'
import { getLocatablePaths } from '@/config/previewElementRegions'
import { isNavigationTargetAllowed } from '../../utils/navigationTargets'
import BetCardTreatmentControls from '../parlays/BetCardTreatmentControls'
import SurfaceContentControls from './SurfaceContentControls'
import styles from './SurfaceInspector.module.css'

const BETTING_MODE_OPTIONS = [
  { value: 'none', label: 'None' },
  { value: 'wapi_kambi', label: 'Kambi WAPI' },
  { value: 'custom', label: 'Custom' },
]

/**
 * Routes to the appropriate inspector based on mode.
 * mode === 'baseline' → BaselineInspector (full token editor)
 * mode === 'surface'  → SurfaceOverridesInspector (manifest-driven)
 */
export default function SurfaceInspector(props) {
  if (props.mode === 'baseline') return <BaselineInspector {...props} />
  return <SurfaceOverridesInspector {...props} />
}

/* ================================================================== */
/* Baseline inspector — full token editor for org-level branding       */
/* ================================================================== */

const BASELINE_SECTIONS = [
  { id: 'basics', label: 'Basics' },
  { id: 'palette', label: 'Palette' },
  { id: 'gradients', label: 'Gradients' },
  { id: 'typography', label: 'Typography' },
  { id: 'layout', label: 'Layout' },
  { id: 'effects', label: 'Effects' },
  { id: 'components', label: 'Components' },
  { id: 'presentation', label: 'Presentation' },
  { id: 'assets', label: 'Assets' },
  { id: 'banner', label: 'Banner' },
]

function BaselineInspector({
  branding,
  onChange, // (path, value) => void
  onUploadBanner, // (file) => void
  isUploading = false,
  focusRequest = null, // { label, paths, nonce } from click-to-edit
  onLocateToken, // (path) => void — flash the path's elements in the preview
}) {
  const [open, setOpen] = useState({ basics: true, palette: true })
  const rootRef = useRef(null)
  const renderedPaths = useMemo(() => new Set(getBaselineTokenPaths()), [])
  const highlightSet = useInspectFocus(
    focusRequest,
    renderedPaths,
    (paths) => setOpen((sections) => ({
      ...sections,
      ...Object.fromEntries(paths.map((path) => [baselineSectionForPath(path), true])),
    })),
    rootRef
  )
  const bannerLinkError = branding.banner?.linkUrl && !isNavigationTargetAllowed(branding.banner.linkUrl)
    ? 'Enter a valid URL or app deep link.'
    : null

  const getValue = (path) => getByPath(branding, path)
  const locateFor = makeLocateFor(onLocateToken, getLocatablePaths())
  const editor = (path, label, kind, options, placeholder) => (
    <TokenField
      key={path}
      path={path}
      label={label}
      kind={kind || inferKindFromPath(path)}
      options={options}
      placeholder={placeholder}
      inheritanceMode="canonical"
      value={getValue(path)}
      inheritedValue=""
      onChange={(next) => onChange(path, next)}
      onReset={() => onChange(path, '')}
      inspectFocus={highlightSet.has(path)}
      onLocate={locateFor(path)}
    />
  )

  return (
    <div className={styles.inspector} data-testid="baseline-inspector" ref={rootRef}>
      <Section
        title="Basics"
        open={open.basics}
        onToggle={() => setOpen((s) => ({ ...s, basics: !s.basics }))}
      >
        <div className={styles.fieldGrid}>
          <TokenField
            path="buttonText"
            label="Default CTA text"
            kind="text"
            inheritanceMode="canonical"
            value={branding.buttonText || ''}
            inheritedValue="Add to Betslip"
            onChange={(next) => onChange('buttonText', next || '')}
            onReset={() => onChange('buttonText', '')}
            inspectFocus={highlightSet.has('buttonText')}
            onLocate={locateFor('buttonText')}
          />
          <TokenField
            path="copyCountLabel"
            label="Copy count label"
            kind="text"
            inheritanceMode="canonical"
            value={branding.copyCountLabel || ''}
            inheritedValue="copied"
            onChange={(next) => onChange('copyCountLabel', next || '')}
            onReset={() => onChange('copyCountLabel', '')}
            inspectFocus={highlightSet.has('copyCountLabel')}
            onLocate={locateFor('copyCountLabel')}
          />
          <TokenField
            path="logoUrl"
            label="Logo URL"
            kind="text"
            inheritanceMode="canonical"
            value={branding.logoUrl || ''}
            inheritedValue=""
            onChange={(next) => onChange('logoUrl', next || '')}
            onReset={() => onChange('logoUrl', '')}
            placeholder="https://cdn.example.com/logo.png"
          />
          <div className={styles.toggleField}>
            <Switch
              label="Dark mode"
              checked={!!branding.darkMode}
              onChange={(checked) => onChange('darkMode', checked)}
            />
          </div>
          <TokenField
            path="fonts.primary"
            label="Primary font family"
            kind="text"
            inheritanceMode="canonical"
            value={branding.fonts?.primary || ''}
            inheritedValue="Inter, sans-serif"
            onChange={(next) => onChange('fonts.primary', next)}
            onReset={() => onChange('fonts.primary', '')}
          />
          <TokenField
            path="fontUrls"
            label="Font source URLs"
            kind="text"
            inheritanceMode="canonical"
            value={Array.isArray(branding.fontUrls) ? branding.fontUrls.join(', ') : ''}
            inheritedValue=""
            onChange={(next) => onChange(
              'fontUrls',
              typeof next === 'string'
                ? next.split(',').map((value) => value.trim()).filter(Boolean)
                : []
            )}
            onReset={() => onChange('fontUrls', [])}
            placeholder="https://fonts.example.com/brand.css"
          />
        </div>
      </Section>

      <Section
        title="Palette"
        open={open.palette}
        onToggle={() => setOpen((s) => ({ ...s, palette: !s.palette }))}
      >
        <div className={styles.fieldGrid}>
          {BASIC_COLOR_FIELDS.map((f) => editor(f.path, f.label, 'color'))}
        </div>
        <h5 className={styles.subhead}>States</h5>
        <div className={styles.fieldGrid}>
          {STATE_COLOR_FIELDS.map((f) => editor(f.path, f.label, 'color'))}
        </div>
      </Section>

      <Section
        title="Gradients"
        open={open.gradients}
        onToggle={() => setOpen((s) => ({ ...s, gradients: !s.gradients }))}
      >
        {GRADIENT_FIELDS.map((f) => editor(f.path, f.label, 'gradient'))}
      </Section>

      <Section
        title="Typography"
        open={open.typography}
        onToggle={() => setOpen((s) => ({ ...s, typography: !s.typography }))}
      >
        {TYPOGRAPHY_GROUPS.map((group) => (
          <div key={group.path} className={styles.typographyGroup}>
            <h5 className={styles.subhead}>{group.label}</h5>
            <div className={styles.fieldGrid}>
              {TYPOGRAPHY_SUBFIELDS.map((sub) => {
                const path = `${group.path}.${sub.key}`
                return (
                  <TokenField
                    key={path}
                    path={path}
                    label={sub.label}
                    kind={sub.kind}
                    inheritanceMode="canonical"
                    options={sub.options}
                    value={getValue(path)}
                    inheritedValue=""
                    onChange={(next) => onChange(path, next)}
                    onReset={() => onChange(path, '')}
                    inspectFocus={highlightSet.has(path)}
                    onLocate={locateFor(path)}
                  />
                )
              })}
            </div>
          </div>
        ))}
      </Section>

      <Section
        title="Layout"
        open={open.layout}
        onToggle={() => setOpen((s) => ({ ...s, layout: !s.layout }))}
      >
        {LAYOUT_GROUPS.map((group) => (
          <div key={group.title} className={styles.layoutGroup}>
            <h5 className={styles.subhead}>{group.title}</h5>
            <div className={styles.fieldGrid}>
              {group.fields.map((f) => editor(f.path, f.label, f.kind || 'size', f.options))}
            </div>
          </div>
        ))}
      </Section>

      <Section
        title="Effects"
        open={open.effects}
        onToggle={() => setOpen((s) => ({ ...s, effects: !s.effects }))}
      >
        <div className={styles.fieldGrid}>
          {EFFECT_FIELDS.map((f) => editor(f.path, f.label, f.kind))}
        </div>
      </Section>

      <Section
        title="Components"
        open={open.components}
        onToggle={() => setOpen((s) => ({ ...s, components: !s.components }))}
      >
        {COMPONENT_GROUPS.map((group) => (
          <div key={group.title} className={styles.layoutGroup}>
            <h5 className={styles.subhead}>{group.title}</h5>
            <div className={styles.fieldGrid}>
              {group.fields.map((f) => editor(f.path, f.label, f.kind, f.options))}
            </div>
          </div>
        ))}
      </Section>

      <Section
        title="Presentation"
        open={open.presentation}
        onToggle={() => setOpen((s) => ({ ...s, presentation: !s.presentation }))}
      >
        {PRESENTATION_GROUPS.map((group) => (
          <div key={group.title} className={styles.layoutGroup}>
            <h5 className={styles.subhead}>{group.title}</h5>
            <div className={styles.fieldGrid}>
              {group.fields.map((f) => editor(f.path, f.label, f.kind, f.options))}
            </div>
          </div>
        ))}
      </Section>

      <Section
        title="Assets"
        open={open.assets}
        onToggle={() => setOpen((s) => ({ ...s, assets: !s.assets }))}
      >
        <div className={styles.fieldGrid}>
          {ASSET_FIELDS.map((f) => editor(f.path, f.label, f.kind, f.options, 'https://cdn.example.com/icon.svg'))}
        </div>
      </Section>

      <Section
        title="Banner"
        open={open.banner}
        onToggle={() => setOpen((s) => ({ ...s, banner: !s.banner }))}
      >
        <div className={styles.fieldGrid}>
          <TokenField
            path="banner.imageUrl"
            label="Image URL"
            kind="text"
            inheritanceMode="canonical"
            value={branding.banner?.imageUrl || ''}
            inheritedValue=""
            onChange={(next) => onChange('banner.imageUrl', next || '')}
            onReset={() => onChange('banner.imageUrl', '')}
          />
          <TokenField
            path="banner.linkUrl"
            label="Link URL"
            kind="text"
            inheritanceMode="canonical"
            value={branding.banner?.linkUrl || ''}
            inheritedValue=""
            onChange={(next) => onChange('banner.linkUrl', next || '')}
            onReset={() => onChange('banner.linkUrl', '')}
          />
          {bannerLinkError ? <p className={styles.validationError}>{bannerLinkError}</p> : null}
          <TokenField
            path="banner.altText"
            label="Alt text"
            kind="text"
            inheritanceMode="canonical"
            value={branding.banner?.altText || ''}
            inheritedValue=""
            onChange={(next) => onChange('banner.altText', next || '')}
            onReset={() => onChange('banner.altText', '')}
          />
        </div>
        <div className={styles.uploadRow}>
          <label className={styles.uploadLabel}>
            <input
              type="file"
              accept="image/*"
              disabled={isUploading}
              onChange={(event) => {
                const file = event.target.files?.[0]
                if (file && onUploadBanner) onUploadBanner(file)
                event.target.value = ''
              }}
              className={styles.uploadInput}
            />
            <span className={styles.uploadTrigger}>
              {isUploading ? 'Uploading…' : 'Upload banner image'}
            </span>
          </label>
        </div>
      </Section>
    </div>
  )
}

/* ================================================================== */
/* Surface overrides inspector — manifest-driven curated editor        */
/* ================================================================== */

function SurfaceOverridesInspector({
  surface,
  contentMode = 'feed',
  overrides = {},
  resolvedInherited, // org baseline (already resolved) used as inherited value source
  onOverrideChange, // (path, value) => void (value undefined = reset)
  onSurfacePatch, // ({field: value}) => void for name/displayType/toggles
  onDeleteSurface,
  onSaveSurface,
  onContentDirtyChange,
  focusRequest = null, // { label, paths, nonce } from click-to-edit
  onLocateToken, // (path) => void — flash the path's elements in the preview
}) {
  const [showAdvanced, setShowAdvanced] = useState(false)
  const rootRef = useRef(null)

  const isPrepack = contentMode === 'prepacks'
  const manifest = useMemo(() => getSurfaceManifest(surface.display_type, contentMode), [surface.display_type, contentMode])

  const allowedPaths = useMemo(
    () => new Set(getAllSurfaceTokenPaths(surface.display_type, contentMode)),
    [surface.display_type, contentMode]
  )

  const advancedPaths = useMemo(
    () => new Set(manifest.advanced.map((field) => field.path)),
    [manifest]
  )

  const highlightSet = useInspectFocus(
    focusRequest,
    allowedPaths,
    (paths) => {
      if (paths.some((path) => advancedPaths.has(path))) setShowAdvanced(true)
    },
    rootRef
  )

  const locateFor = makeLocateFor(onLocateToken, getLocatablePaths())

  const offManifestOverrides = useMemo(() => {
    const offList = []
    walkPaths(overrides, '', (path, value) => {
      if (!allowedPaths.has(path)) offList.push({ path, value })
    })
    return offList
  }, [overrides, allowedPaths])

  return (
    <div className={styles.inspector} data-testid="surface-inspector" ref={rootRef}>
      <header className={styles.surfaceHeader}>
        <div>
          <div className={styles.surfaceHeaderLabel}>{labelForDisplayType(surface.display_type)}</div>
          <h3 className={styles.surfaceHeaderTitle}>{surface.name}</h3>
          <code className={styles.surfaceSlug}>{surface.slug}</code>
        </div>
        <Button
          variant="ghost"
          size="sm"
          onClick={onDeleteSurface}
          data-testid="surface-inspector-delete"
          title="Delete surface"
        >
          <Trash2 size={14} />
        </Button>
      </header>

      <Section title="Surface settings" open onToggle={() => {}} noToggle>
        <div className={styles.surfaceSettingsGrid}>
          <div className={styles.toggleField}>
            <Switch
              label="Enabled"
              checked={!!surface.is_enabled}
              onChange={(checked) => onSurfacePatch({ is_enabled: checked })}
            />
            <HelpTip text="Disabled surfaces still exist but don't receive new posts." placement="top" />
          </div>
          {!isPrepack && <>
          <div className={styles.toggleField}>
            <Switch
              label="Include by default"
              checked={!!surface.include_by_default}
              onChange={(checked) => onSurfacePatch({ include_by_default: checked })}
            />
            <HelpTip text="New posts automatically target this surface unless deselected." placement="top" />
          </div>
          <div className={styles.toggleField}>
            <Switch
              label="Show betting controls"
              checked={!!surface.show_betting_chrome}
              onChange={(checked) => onSurfacePatch({ show_betting_chrome: checked })}
            />
            <HelpTip text="Show bet legs, odds, and the add-to-betslip action." placement="top" />
          </div>
          </>}
          <label className={styles.selectField}>
            <span>Betting mode</span>
            <Select
              value={surface.betting_mode}
              onChange={(e) => onSurfacePatch({ betting_mode: e.target.value })}
            >
              {BETTING_MODE_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </Select>
          </label>
        </div>
      </Section>

      <Section title="Content" open onToggle={() => {}} noToggle>
        <SurfaceContentControls
          surface={surface}
          contentMode={contentMode}
          onSave={onSaveSurface}
          onDirtyChange={onContentDirtyChange}
        />
      </Section>

      {!isPrepack && (
      <Section title="Bet cards" open onToggle={() => {}} noToggle>
        <BetCardTreatmentControls surface={surface} onSave={onSaveSurface} />
      </Section>
      )}

      <Section title="Appearance" open onToggle={() => {}} noToggle>
        <p className={styles.sectionHint}>{manifest.description}</p>
        <div className={styles.fieldGrid}>
          {manifest.essential.map((field) => (
            <TokenField
              key={field.path}
              path={field.path}
              label={field.label}
              kind={field.kind}
              options={field.options}
              value={getByPath(overrides, field.path)}
              inheritedValue={String(getByPath(resolvedInherited, field.path) ?? '')}
              onChange={(next) => onOverrideChange(field.path, next)}
              onReset={() => onOverrideChange(field.path, undefined)}
              inspectFocus={highlightSet.has(field.path)}
              onLocate={locateFor(field.path)}
            />
          ))}
        </div>
      </Section>

      {manifest.advanced.length > 0 && (
        <Section
          title={`Advanced (${manifest.advanced.length})`}
          open={showAdvanced}
          onToggle={() => setShowAdvanced((v) => !v)}
        >
          <div className={styles.fieldGrid}>
            {manifest.advanced.map((field) => (
              <TokenField
                key={field.path}
                path={field.path}
                label={field.label}
                kind={field.kind}
                options={field.options}
                value={getByPath(overrides, field.path)}
                inheritedValue={String(getByPath(resolvedInherited, field.path) ?? '')}
                onChange={(next) => onOverrideChange(field.path, next)}
                onReset={() => onOverrideChange(field.path, undefined)}
                inspectFocus={highlightSet.has(field.path)}
                onLocate={locateFor(field.path)}
              />
            ))}
          </div>
        </Section>
      )}

      {offManifestOverrides.length > 0 && (
        <AdvancedDisclosure label={`Advanced: Other overrides (${offManifestOverrides.length})`}>
          <p className={styles.sectionHint}>
            {isPrepack
              ? 'These settings are preserved for Feed content or other renderers; they do not style this prepack preview.'
              : "These overrides were set outside this surface's usual token set. They still apply."}
          </p>
          {!isPrepack && (
          <div className={styles.fieldGrid}>
            {offManifestOverrides.map(({ path }) => (
              <TokenField
                key={path}
                path={path}
                label={path}
                kind={inferKindFromPath(path)}
                value={getByPath(overrides, path)}
                inheritedValue={String(getByPath(resolvedInherited, path) ?? '')}
                onChange={(next) => onOverrideChange(path, next)}
                onReset={() => onOverrideChange(path, undefined)}
              />
            ))}
          </div>
          )}
        </AdvancedDisclosure>
      )}
    </div>
  )
}

/* ================================================================== */
/* Shared utilities                                                    */
/* ================================================================== */

function Section({ title, open, onToggle, children, noToggle = false }) {
  return (
    <section className={styles.section}>
      <button
        type="button"
        className={styles.sectionHeader}
        onClick={noToggle ? undefined : onToggle}
        aria-expanded={open}
        data-notoggle={noToggle}
      >
        <span className={styles.sectionTitle}>{title}</span>
        {!noToggle &&
          (open ? <ChevronDown size={14} /> : <ChevronRight size={14} />)}
      </button>
      {open && <div className={styles.sectionBody}>{children}</div>}
    </section>
  )
}

/**
 * Per-field "Locate in preview" affordance gate: only paths some preview
 * region can resolve back to an element get a locate callback.
 */
function makeLocateFor(onLocateToken, locatablePaths) {
  return (path) =>
    onLocateToken && locatablePaths.has(path) ? () => onLocateToken(path) : undefined
}

function baselineSectionForPath(path) {
  if (path.startsWith('colors.')) return 'palette'
  if (path.startsWith('gradients.')) return 'gradients'
  if (path.startsWith('typography.')) return 'typography'
  if (path.startsWith('layout.')) return 'layout'
  if (path.startsWith('effects.')) return 'effects'
  if (path.startsWith('components.')) return 'components'
  if (path.startsWith('presentation.')) return 'presentation'
  if (path.startsWith('assets.')) return 'assets'
  if (path.startsWith('banner.')) return 'banner'
  return 'basics'
}

function getByPath(source, path) {
  if (!source) return undefined
  return path.split('.').reduce((cur, part) => {
    if (cur == null || typeof cur !== 'object') return undefined
    return cur[part]
  }, source)
}

function walkPaths(source, prefix, visit) {
  if (!source || typeof source !== 'object' || Array.isArray(source)) return
  for (const [key, value] of Object.entries(source)) {
    const next = prefix ? `${prefix}.${key}` : key
    if (value && typeof value === 'object' && !Array.isArray(value)) {
      walkPaths(value, next, visit)
    } else if (value !== undefined && value !== null && value !== '') {
      visit(next, value)
    }
  }
}
